// D:\QR\QR-WEB\dashboard\src\components\Business\DepositForm.jsx
// Версия: 2.0 с полной API интеграцией
// Дата: 21.11.2025 15:50

import React, { useState } from 'react';
import './DepositForm.css';

const DepositForm = () => {
  const [amount, setAmount] = useState('');
  const [bank, setBank] = useState('Wise');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [calculation, setCalculation] = useState(null);

  const MIN_EUR = 500;
  const MAX_EUR = 10000;
  const EXCHANGE_RATE = 0.9975;
  const OUR_MARGIN = 0.05; // 5%
  const BANK_FEE = 5; // €5

  // Расчёт при вводе суммы
  const handleAmountChange = (e) => {
    const value = e.target.value;
    setAmount(value);
    setError(null);

    if (value && !isNaN(value)) {
      const eur = parseFloat(value);
      
      // Валидация
      if (eur < MIN_EUR) {
        setError(`Минимум €${MIN_EUR}`);
        setCalculation(null);
        return;
      }
      if (eur > MAX_EUR) {
        setError(`Максимум €${MAX_EUR}`);
        setCalculation(null);
        return;
      }

      // Расчёт
      const rateWithMargin = EXCHANGE_RATE * (1 - OUR_MARGIN);
      const usdt = eur * rateWithMargin - (BANK_FEE * EXCHANGE_RATE);
      
      setCalculation({
        eur: eur.toFixed(2),
        usdt: usdt.toFixed(2),
        rate: rateWithMargin.toFixed(4),
        margin: (eur * OUR_MARGIN).toFixed(2),
        fee: BANK_FEE
      });
    } else {
      setCalculation(null);
    }
  };

  // Создание заявки на пополнение
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!amount || isNaN(amount)) {
      setError('Введите сумму');
      return;
    }

    const eur = parseFloat(amount);
    if (eur < MIN_EUR || eur > MAX_EUR) {
      setError(`Сумма должна быть от €${MIN_EUR} до €${MAX_EUR}`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        setError('Ошибка: токен не найден. Авторизуйтесь заново.');
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/business/exchange/deposit/create`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            amountEur: parseFloat(amount),
            bankName: bank
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Ошибка при создании заявки');
      }

      setSuccess(
        `✅ Заявка создана успешно!\n` +
        `Номер: ${data.data.reference_invoice}\n` +
        `Вы получите: ${calculation.usdt} USDT`
      );
      
      setAmount('');
      setCalculation(null);
      
      // Очистить success через 5 секунд
      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      setError(`❌ Ошибка: ${err.message}`);
      console.error('Deposit error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="deposit-form">
      <h2>💰 Купить DAR за EUR</h2>

      {/* Сумма */}
      <div className="form-group">
        <label>Сумма EUR</label>
        <div className="input-group">
          <input
            type="number"
            value={amount}
            onChange={handleAmountChange}
            placeholder={`${MIN_EUR} - ${MAX_EUR} EUR`}
            min={MIN_EUR}
            max={MAX_EUR}
            step="1"
            disabled={loading}
          />
          <span className="currency">€</span>
        </div>
        <small>Мин: €{MIN_EUR} | Макс: €{MAX_EUR}</small>
      </div>

      {/* Банк */}
      <div className="form-group">
        <label>Банк-партнёр</label>
        <select 
          value={bank} 
          onChange={(e) => setBank(e.target.value)}
          disabled={loading}
        >
          <option value="Wise">Wise (быстро и дёшево)</option>
          <option value="SEPA">SEPA Transfer</option>
        </select>
      </div>

      {/* Расчёт */}
      {calculation && (
        <div className="calculation-box">
          <div className="calc-row">
            <span>Вы платите:</span>
            <strong>€{calculation.eur}</strong>
          </div>
          <div className="calc-row">
            <span>Курс (спред 5%):</span>
            <strong>{calculation.rate}</strong>
          </div>
          <div className="calc-row">
            <span>Наша комиссия:</span>
            <strong>€{calculation.margin}</strong>
          </div>
          <div className="calc-row">
            <span>Комиссия банка:</span>
            <strong>€{calculation.fee}</strong>
          </div>
          <div className="calc-row total">
            <span>Вы получите:</span>
            <strong className="usdt">{calculation.usdt} DAR</strong>
          </div>
        </div>
      )}

      {/* Ошибка */}
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      {/* Успех */}
      {success && (
        <div className="alert alert-success">
          {success}
        </div>
      )}

      {/* Кнопка */}
      <button 
        type="submit" 
        disabled={loading || !amount || !calculation}
        className="btn-submit"
      >
        {loading ? '⏳ Создание заявки...' : '✅ Создать заявку'}
      </button>

      {/* Справка */}
      <div className="help-box">
        <h4>ℹ️ Как это работает?</h4>
        <ol>
          <li>Введите сумму EUR (минимум €500)</li>
          <li>Увидите расчёт сколько USDT получите</li>
          <li>Нажмите "Создать заявку"</li>
          <li>Получите реквизиты для платежа</li>
          <li>Переведите деньги через указанный банк</li>
          <li>После подтверждения - деньги на счёт</li>
        </ol>
      </div>
    </form>
  );
};

export default DepositForm;
